import PopOver from 'bootstrap-vue/src/utils/popover.class';
import moment from 'moment';

import { HOME_DUTY_ABSENCE_NAME, HOME_OFFICE_ABSENCE_NAME, toClock } from '@/utils';
import authenticationService from './authentication.service';

export class CalendarService {
  public workdayToCalendarEvent(workday: any) {
    return {
      allDay: false,
      color: '#17a2b8',
      textColor: 'white',
      workdayId: workday.id,
      workdayStart: workday.start,
      workdayEnd: workday.end,
      overtimeAccepted: workday.overtimeAccepted,
      overtimeLimit: workday.overtimeLimit,
      title: this.workdayTitle(workday),
      ...this.workdayRange(workday),
      user: workday.user
    };
  }

  public userDayOffToCalendarEvent(dayOff: any) {
    const absence = dayOff.absence ? dayOff.absence.name : undefined;
    return {
      ...this.icon(dayOff),
      ...this.range(dayOff),
      allDay: true,
      title: this.userTitle(dayOff),
      className: this.getClasses(dayOff),
      reason:
        authenticationService.isAdmin() || authenticationService.isHr() || this.isAuthor(dayOff) ? dayOff.reason : '',
      dayOffId: dayOff.id,
      items: dayOff.items,
      user: dayOff.user,
      absenceId: dayOff.absenceId,
      hourStart: dayOff.hourStart,
      minuteStart: dayOff.minuteStart,
      hourEnd: dayOff.hourEnd,
      minuteEnd: dayOff.minuteEnd,
      hrAccept: dayOff.hrAccept,
      managerAccept: dayOff.managerAccept,
      absence
    };
  }

  public dayOffToCalendarEvent(dayOff: any) {
    if (!dayOff.name) {
      return this._dayOffToCalendarEvent(dayOff);
    } else if (dayOff.name) {
      return this.holidayToCalendarEvent(dayOff);
    }
  }

  public showDayOffPopup(event: any, element: any, root: any) {
    const start = moment(event.start.toDate()).format('DD.MM.YYYY');
    const end = event.end
      ? ' - ' +
        moment(event.end.toDate())
          .subtract(1, 'days')
          .format('DD.MM.YYYY')
      : ' ';
    const hourStart = event.hourStart ? toClock(event.hourStart) + ':' + toClock(event.minuteStart || 0) : '';
    const hourEnd = event.hourEnd ? ' - ' + toClock(event.hourEnd) + ':' + toClock(event.minuteEnd || 0) + '\n' : '\n';
    const status =
      (event.managerAccept && event.hrAccept
        ? 'Zaakceptowano'
        : event.managerAccept
          ? 'Zaakceptowano DW'
          : event.hrAccept
            ? 'Zaakceptowano RN'
            : 'Oczekuje') + '\n';
    const content = status + start + end + hourStart + hourEnd;
    let hrContent = '';
    if (authenticationService.is('admin', 'hr')) {
      hrContent = event.reason;
      if (event.absence !== undefined) {
        hrContent += '\n' + event.absence;
      }
    }
    const config = {
      title: event.userName,
      content: content + hrContent,
      placement: 'auto', // can use any of Popover's placements(top, bottom, right, left etc)
      container: 'null', // can pass in the id of a container here, other wise just appends to body
      boundary: 'scrollParent',
      boundaryPadding: 5,
      delay: 0,
      offset: 0,
      animation: true,
      trigger: 'hover', // can be 'click', 'hover' or 'focus'
      html: false // if you want HTML in your content set to true.
    };
    return new PopOver(element[0], config, root);
  }

  /**
   * Dodawanie konkretnych ikon do pozycji w kalendarzu
   *
   * @param {*} event
   * @param {*} element
   * @memberof CalendarService
   */
  public prependDayOffIcons(event: any, element: any): void {
    if (event.absence === HOME_OFFICE_ABSENCE_NAME /* absence.name */) {
      element.find('.fc-title').prepend(`<i class="fa fa-fw fa-home"></i>`);
    }
    if (event.absence === HOME_DUTY_ABSENCE_NAME /* absence.name */) {
      element.find('.fc-title').prepend(`<i class="fa fa-fw fa-phone-square"></i>`);
    }

    if (event.hourStart && event.hourEnd) {
      element.find('.fc-title').prepend(`<i class="far fa-fw fa-clock"></i>`);
    }

    element.find('.fc-title').prepend(`<i class="${event.iconPrefix} fa-fw fa-${event.icon}"></i>`);
  }

  /**
   * Funkcja zwracająca nazwę zdefiniowanej w ustawieniach pracy zdalnej
   *
   * @private
   * @deprecated Proszę używać `const HOME_OFFICE_ABSENCE_NAME`
   * @return {*}  {string}
   * @memberof CalendarService
   */
  private homeOffice(): string {
    return HOME_OFFICE_ABSENCE_NAME;
  }

  private _dayOffToCalendarEvent(dayOff: any) {
    const absence = dayOff.absence ? dayOff.absence.name : undefined;
    return {
      ...this.icon(dayOff),
      ...this.range(dayOff),
      userID: dayOff.user.id,
      allDay: true,
      title: this.title(dayOff),
      className: this.getClasses(dayOff),
      dayOffId: dayOff.id,
      userName: dayOff.user.firstName + ' ' + dayOff.user.lastName,
      hourStart: dayOff.hourStart,
      minuteStart: dayOff.minuteStart,
      hourEnd: dayOff.hourEnd,
      minuteEnd: dayOff.minuteEnd,
      reason: dayOff.reason,
      hrAccept: dayOff.hrAccept,
      managerAccept: dayOff.managerAccept,
      absence
    };
  }

  private range(dayOff: any) {
    const dates = dayOff.items.map((item: any) => moment(item.date));
    const start = moment.min(dates);
    const end = moment.max(dates);
    return { start, end: end !== start ? end.add(1, 'd') : end };
  }

  private workdayRange(workday: any) {
    const start = moment(workday.date)
      .utc()
      .add(workday.start, 'minutes');
    const end = moment(workday.date)
      .utc()
      .add(workday.end, 'minutes');
    return {
      start,
      end
    };
  }

  private title(dayOff: any) {
    return `${dayOff.user.firstName} ${dayOff.user.lastName}`;
  }

  /**
   * Funkcja zwracająca nazwę pozycji w kalendarzu zakładki **Mój Profil**
   *
   * @private
   * @param {*} dayOff
   * @return {*}  {string}
   * @memberof CalendarService
   */
  private userTitle(dayOff: any): string {
    if (dayOff.absence && dayOff.absence.name === HOME_OFFICE_ABSENCE_NAME) {
      return HOME_OFFICE_ABSENCE_NAME;
    }
    if (dayOff.absence && dayOff.absence.name === HOME_DUTY_ABSENCE_NAME) {
      return HOME_DUTY_ABSENCE_NAME;
    }
    return dayOff.hourStart && dayOff.hourEnd ? 'Wyjście' : 'Dzień wolny';
  }

  private workdayTitle(workday: any) {
    if (workday.end) {
      const timeAtWork = moment(workday.date)
        .add(workday.end - workday.start, 'minutes')
        .utc()
        .format('LT');
      return '\nCzas w pracy: ' + timeAtWork + '\n' + workday.company.name;
    }
    return '\n' + workday.company.name;
  }

  /**
   * Ustala klasy styli kolorystycznie powiązane z rodzajem obecności/nieobecności
   *
   * @private
   * @param {*} dayOff
   * @param {boolean} [forceSelf]
   * @return {*}  {string}
   * @memberof CalendarService
   */
  private getClasses(dayOff: any, forceSelf?: boolean): string {
    const classes: string[] = [
      this.isAuthor(dayOff) || forceSelf ? 'self-person' : 'other-person',
      dayOff.absence &&
      (dayOff.absence.name === HOME_OFFICE_ABSENCE_NAME || dayOff.absence.name === HOME_DUTY_ABSENCE_NAME)
        ? 'home-office'
        : 'office',
      this.isAccepted(dayOff) ? 'accepted' : 'not-accepted'
    ];
    return classes.join(' ');
  }

  private icon(dayOff: any) {
    if (dayOff.accepted) {
      return { icon: 'check-double', iconPrefix: 'fas' };
    }
    if (dayOff.hrAccept || dayOff.managerAccept) {
      return { icon: 'check', iconPrefix: 'fa' };
    }
    return { icon: 'question', iconPrefix: 'fa' };
  }

  private isAccepted(dayOff: any) {
    const accepted = dayOff.accepted;
    const hrAccepted = dayOff.hrAccept && authenticationService.is('hr');
    const managerAccepted = dayOff.managerAccept && authenticationService.is('manager');
    return accepted || hrAccepted || managerAccepted;
  }

  private isAuthor(dayOff: any) {
    return dayOff.user && dayOff.user.id === authenticationService.userId();
  }

  private holidayToCalendarEvent(dayOff: any) {
    return {
      title: dayOff.name,
      allDay: true,
      start: dayOff.date,
      textColor: '#000000',
      color: dayOff.color,
      rendering: 'background'
    };
  }
}

export default new CalendarService();
